/**
 * Q&A(スタッフページ。2026-09-17)。qa_items/{自動ID}。読むのは admin・viewer・operator、書くのは admin だけ(firestore.rules)。
 * 変更は監査ログ(target_type=qa)に残す。
 */
import { addDoc, collection, deleteDoc, doc, getDocs, serverTimestamp, updateDoc, writeBatch } from 'firebase/firestore';
import { writeAuditLog } from '@core/lib/auditLog';
import { getDb } from '@core/lib/firebase';

export type QaItem = {
  id: string;
  category: string;
  question: string;
  answer: string;
  /** 小さいほど上。同じならカテゴリ → 質問の順 */
  sort_order: number;
  created_by?: string;
  created_at?: unknown;
  updated_by?: string;
  updated_at?: unknown;
};

export type QaInput = Pick<QaItem, 'category' | 'question' | 'answer' | 'sort_order'>;

const COL = 'qa_items';

function validate(input: QaInput): QaInput {
  const v = { category: input.category.trim(), question: input.question.trim(), answer: input.answer.replace(/\r\n?/g, '\n').trim(), sort_order: input.sort_order };
  if (!v.category) throw new Error('カテゴリを入力してください');
  if (!v.question) throw new Error('質問を入力してください');
  if (!v.answer) throw new Error('回答を入力してください');
  if (!Number.isInteger(v.sort_order) || v.sort_order < 0) throw new Error('表示順は0以上の整数にしてください');
  return v;
}

export async function fetchQaItems(): Promise<QaItem[]> {
  const snap = await getDocs(collection(getDb(), COL));
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<QaItem, 'id'>) }));
}

export async function createQa(input: QaInput, userId: string): Promise<void> {
  const v = validate(input);
  const ref = await addDoc(collection(getDb(), COL), { ...v, created_by: userId, created_at: serverTimestamp(), updated_by: userId, updated_at: serverTimestamp() });
  await writeAuditLog({ targetType: 'qa', targetId: ref.id, action: 'qa-create', by: userId, changes: [`追加: 【${v.category}】${v.question}`] });
}

export async function updateQa(item: QaItem, input: QaInput, userId: string): Promise<void> {
  const v = validate(input);
  await updateDoc(doc(getDb(), COL, item.id), { ...v, updated_by: userId, updated_at: serverTimestamp() });
  const lines: string[] = [];
  if (item.category !== v.category) lines.push(`カテゴリ: ${item.category} → ${v.category}`);
  if (item.question !== v.question) lines.push(`質問: ${item.question} → ${v.question}`);
  if (item.answer !== v.answer) lines.push(`回答を変更: ${v.question}`);
  if (item.sort_order !== v.sort_order) lines.push(`表示順: ${item.sort_order} → ${v.sort_order}`);
  await writeAuditLog({ targetType: 'qa', targetId: item.id, action: 'qa-update', by: userId, before: { ...item }, changes: lines });
}

export async function deleteQa(item: QaItem, userId: string): Promise<void> {
  await deleteDoc(doc(getDb(), COL, item.id));
  await writeAuditLog({ targetType: 'qa', targetId: item.id, action: 'qa-delete', by: userId, before: { category: item.category, question: item.question, answer: item.answer, sort_order: item.sort_order } });
}

/**
 * CSV 一括の実行。400件ずつのバッチで書く(途中で失敗したら、そのバッチより前の分だけ反映される)。
 * 削除した項目は監査ログに中身を残す(取り消したいときに戻せるように)
 */
export async function applyQaImport(
  plan: { creates: QaInput[]; updates: { item: QaItem; next: QaInput }[]; deletes: QaItem[] },
  userId: string,
): Promise<{ created: number; updated: number; deleted: number }> {
  const db = getDb();
  const ops: ((b: ReturnType<typeof writeBatch>) => void)[] = [
    ...plan.creates.map((c) => (b: ReturnType<typeof writeBatch>) => b.set(doc(collection(db, COL)), { ...validate(c), created_by: userId, created_at: serverTimestamp(), updated_by: userId, updated_at: serverTimestamp() })),
    ...plan.updates.map((u) => (b: ReturnType<typeof writeBatch>) => b.update(doc(db, COL, u.item.id), { ...validate(u.next), updated_by: userId, updated_at: serverTimestamp() })),
    ...plan.deletes.map((d) => (b: ReturnType<typeof writeBatch>) => b.delete(doc(db, COL, d.id))),
  ];
  for (let i = 0; i < ops.length; i += 400) {
    const batch = writeBatch(db);
    ops.slice(i, i + 400).forEach((op) => op(batch));
    await batch.commit();
  }
  await writeAuditLog({
    targetType: 'qa',
    targetId: 'csv-import',
    action: 'qa-csv-import',
    by: userId,
    before: plan.deletes.length > 0 ? { deleted: plan.deletes.map((d) => ({ category: d.category, question: d.question, answer: d.answer, sort_order: d.sort_order })) } : null,
    changes: [`CSV一括: 追加 ${plan.creates.length}件・変更 ${plan.updates.length}件・削除 ${plan.deletes.length}件`],
  });
  return { created: plan.creates.length, updated: plan.updates.length, deleted: plan.deletes.length };
}
